import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Url } from '../../../common';
import { Language } from '../../enums';
import { ClientConfigService } from '../client-config';
import { CustomHttpParamEncoder } from './custom-http-param-encoder';
import { shouldCheckKidsToken } from './mpx-http-context-tokens';
import {
  MpxAddToMyListBody,
  MpxAddToMyListSuccessResponse,
  MpxBookmarkItem,
  MpxConcurrencyStreamUnlockResponse,
  MpxConcurrencyStreamUpdateResponse,
  MpxCreateMyListSuccessResponse,
  MpxDeleteFromMyListSuccessResponse,
  MpxMyList,
  MpxMyListItemState,
  MpxProgram,
  SearchRange,
} from './mpx.interface';

@Injectable()
export class PlayerAndPersonaMpxService {
  private readonly mpxPublicUrlFormat = 'smil';

  constructor(private readonly httpClient: HttpClient, private readonly clientConfigService: ClientConfigService) {}

  public getStreamUrlInSmilFormat$(publicUrl: Url, mpxStreamingToken: string, clientId: string | undefined): Observable<string> {
    const params = new HttpParams({
      fromObject: {
        auth: mpxStreamingToken,
        format: this.mpxPublicUrlFormat,
        // providing tracking will return us the releaseId alias "pid"
        tracking: 'true',
        clientId,
      },
    });
    return this.httpClient.get(publicUrl, { params, responseType: 'text' });
  }

  public getMpxProgram$(publicId: string): Observable<MpxProgram> {
    const params = new HttpParams({
      fromObject: {
        byId: publicId,
      },
    });
    return this.httpClient.get<any>(this.clientConfigService.mpxAllProgramsFeedUrl, {
      params,
      context: new HttpContext().set(shouldCheckKidsToken, true),
    });
  }

  public getEncodedThumbnails$(imageStreamTag: Element): Observable<{ thumbnails: string[] }> {
    return this.httpClient.get<{ thumbnails: string[] }>(imageStreamTag.getAttribute('src')).pipe(
      // don't show thumbnails if there was any error getting them
      catchError((error) => {
        console.error('Failed to get thumbnail images');
        // eslint-disable-next-line no-console
        console.log(error);
        return of({ thumbnails: [] });
      })
    );
  }

  public getSeriesByGuid$(guid: string, lang?: Language): Observable<MpxProgram> {
    return this.getSeriesByGuids$([guid], lang);
  }

  public getSeriesByGuids$(guids: string[], lang?: Language): Observable<MpxProgram> {
    let params = new HttpParams({
      fromObject: {
        byGuid: guids.join('|'),
        fields: 'guid,thumbnails,titleLocalized,title',
      },
    });
    if (lang) {
      params = params.append('lang', lang);
    }
    return this.httpClient.get<MpxProgram>(this.clientConfigService.mpxAllSeriesFeedUrl, {
      params,
      context: new HttpContext().set(shouldCheckKidsToken, true),
    });
  }

  public getEpisodesBySeasonId$(seasonId: string, range: SearchRange, count: boolean = false): Observable<MpxProgram> {
    const params = new HttpParams({
      fromObject: {
        bytvseasonid: seasonId,
        range: `${range.startIndex}-${range.endIndex}`,
        count: `${count}`,
      },
    });
    return this.httpClient.get<MpxProgram>(this.clientConfigService.mpxAllEpisodesFeedUrl, { params });
  }

  public setBookmark$(subjectId: string, currentTime: number, duration: number, seriesId?: string, seasonId?: string): Observable<unknown> {
    const httpOptions = {
      headers: new HttpHeaders({}),
      params: new HttpParams({
        fromObject: {
          pipeline: 'audience',
        },
      }),
    };

    const body = {
      subjectId,
      time: currentTime,
      duration,
      seriesId: undefined,
      seasonId: undefined,
    };
    if (seriesId) {
      body.seriesId = seriesId;
    }
    if (seasonId) {
      body.seasonId = seasonId;
    }

    return this.httpClient.post(new URL('bookmark', this.clientConfigService.mpxBookmarkServiceBaseUrl).href, body, httpOptions);
  }

  public getBookmarks() {
    const httpOptions = {
      headers: new HttpHeaders({}),
      params: new HttpParams({
        fromObject: {
          pipeline: 'audience',
          percentComplete: '0~100',
        },
      }),
      context: new HttpContext().set(shouldCheckKidsToken, true),
    };

    return this.httpClient.get<Array<MpxBookmarkItem>>(
      new URL('bookmarks', this.clientConfigService.mpxBookmarkServiceBaseUrl).href,
      httpOptions
    );
  }

  public updateConcurrencyStreamingData$(
    concurrencyServiceUrl: string,
    cliendId: string,
    lockId: string,
    lockSequenceToken: string,
    encryptedLock: string
  ): Observable<MpxConcurrencyStreamUpdateResponse> {
    const url = `${concurrencyServiceUrl}/web/Concurrency/update`;
    // Custom param encoder needed, because the default http encoder doesn't encode properly.
    const httpOptions = {
      params: new HttpParams({
        encoder: new CustomHttpParamEncoder(),
        fromObject: {
          schema: '1.0',
          _clientId: cliendId,
          _id: lockId,
          _sequenceToken: lockSequenceToken,
          _encryptedLock: encryptedLock,
          form: 'json',
        },
      }),
    };
    return this.httpClient.get<MpxConcurrencyStreamUpdateResponse>(url, httpOptions);
  }

  public unlockConcurrencyStreamingData$(
    concurrencyServiceUrl: string,
    cliendId: string,
    lockId: string,
    lockSequenceToken: string,
    encryptedLock: string
  ): Observable<MpxConcurrencyStreamUnlockResponse> {
    const url = `${concurrencyServiceUrl}/web/Concurrency/unlock`;
    // Custom param encoder needed, because the default http encoder doesn't encode properly.
    const httpOptions = {
      params: new HttpParams({
        encoder: new CustomHttpParamEncoder(),
        fromObject: {
          schema: '1.0',
          _clientId: cliendId,
          _id: lockId,
          _sequenceToken: lockSequenceToken,
          _encryptedLock: encryptedLock,
          form: 'json',
        },
      }),
    };
    return this.httpClient.get<MpxConcurrencyStreamUnlockResponse>(url, httpOptions);
  }

  public getMyList$(personaId: string): Observable<MpxMyList> {
    const params = new HttpParams({
      fromObject: {
        schema: '1.0',
        form: 'json',
        account: this.clientConfigService.mpxAccountId,
        byTitle: `Watchlist-${personaId}`,
        fields: 'title,id,items,items.title,items.aboutId,items.updated',
      },
    });
    const headers = new HttpHeaders({});
    return this.httpClient.get<MpxMyList>(this.clientConfigService.mpxUserListServiceUrl, { params, headers });
  }

  public createMyList$(personaId: string): Observable<MpxCreateMyListSuccessResponse> {
    const body: { title: string; ownerId: string } = {
      title: `Watchlist-${personaId}`,
      ownerId: this.clientConfigService.mpxAccountId,
    };
    const params = new HttpParams({
      fromObject: {
        schema: '1.0',
        form: 'json',
        account: this.clientConfigService.mpxAccountId,
      },
    });
    const headers = new HttpHeaders({});
    return this.httpClient.post<MpxCreateMyListSuccessResponse>(this.clientConfigService.mpxUserListServiceUrl, body, {
      params,
      headers,
    });
  }

  public addToMyList$(programTitle: string, programAvailabilityIdUrl: string, myListId: string): Observable<MpxAddToMyListSuccessResponse> {
    const body: MpxAddToMyListBody = {
      $xmlns: {
        pluserlistitem: 'http://xml.theplatform.com/userprofile/data/UserListItem',
      },
      title: programTitle,
      pluserlistitem$userListId: myListId,
      pluserlistitem$aboutId: programAvailabilityIdUrl,
    };
    const params = new HttpParams({
      fromObject: {
        schema: '1.0',
        form: 'json',
        account: this.clientConfigService.mpxAccountId,
      },
    });
    const headers = new HttpHeaders({});
    return this.httpClient.post<MpxAddToMyListSuccessResponse>(this.clientConfigService.mpxUserListItemServiceUrl, body, {
      params,
      headers,
    });
  }

  public removeFromMyList$(listItemId: string): Observable<MpxDeleteFromMyListSuccessResponse> {
    const params = new HttpParams({
      fromObject: {
        schema: '1.0',
        form: 'json',
        account: this.clientConfigService.mpxAccountId,
      },
    });
    const headers = new HttpHeaders({});

    const urlWithListitemId = `${this.clientConfigService.mpxUserListItemServiceUrl}/${listItemId}`;
    return this.httpClient.delete<MpxDeleteFromMyListSuccessResponse>(urlWithListitemId, {
      params,
      headers,
    });
  }

  public getStateOfMyListItem$(myListId: string, contentId: string): Observable<MpxMyListItemState> {
    const params = new HttpParams({
      fromObject: {
        schema: '1.0',
        form: 'json',
        account: this.clientConfigService.mpxAccountId,
        byUserListId: myListId,
        byAboutId: contentId,
      },
    });
    const headers = new HttpHeaders({});
    return this.httpClient.get<any>(this.clientConfigService.mpxUserListItemServiceUrl, { params, headers });
  }
}
